import { flexRender, Header, HeaderGroup, PaginationState, Row, Table } from '@tanstack/react-table';
import { EllipsedTextWithTooltip } from 'components/EllipsedTextWithTooltip';
import { SelectChangeEvent } from '@mui/material';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { TablePagination } from 'components/Pagination/TablePagination';
import { getSubtitleFromHeader } from 'components/table/DataTableUtil';
import { useIntl } from 'react-intl';
import { useDataTableContext } from 'components/table/context/DataTableContext';
import { cn } from 'utils/classNamesTailwind';
import { TbIcon } from 'components/Figma';

interface DataTableRendererProps<T> {
  table: Table<T>;
  pagination: PaginationState;
  onRowClick?: (row: Row<T>) => void;
  testIdPrefix?: string;
  hidePagination?: boolean;
}

export function DataTableRenderer<T>({
  table,
  pagination,
  onRowClick = undefined,
  testIdPrefix = '',
  hidePagination = false,
}: DataTableRendererProps<T>) {
  const intl = useIntl();
  const { setSortedColumnHeader } = useDataTableContext();
  const [mouseHoverState, setMouseHoverState] = useState<Record<string, boolean>>({});

  const allHeaders = table.getHeaderGroups().flatMap(hg => hg.headers);

  useEffect(() => {
    table.getHeaderGroups().forEach(hg =>
      hg.headers.forEach(h => {
        if (setSortedColumnHeader && h.column.getIsSorted()) {
          setSortedColumnHeader(getSubtitleFromHeader(h, intl));
        }
      })
    );
  }, [intl, allHeaders]);

  const ellipseIfNotElement = (displayItem: string | ReactElement) => {
    if (typeof displayItem === 'string') {
      return <EllipsedTextWithTooltip className="line-clamp-2" text={displayItem} />;
    } else {
      return displayItem;
    }
  };

  const getHeaderText = <T,>(header: Header<T, unknown>): ReactElement => {
    const columnHeaderContext = header.column.columnDef.header;
    if (typeof columnHeaderContext === 'function') {
      const returnedHeader = columnHeaderContext(header.getContext()) as ReactElement | string;
      return ellipseIfNotElement(returnedHeader);
    }
    return ellipseIfNotElement(columnHeaderContext as ReactElement | string);
  };

  const updateSortingState = (event: React.MouseEvent<HTMLDivElement>, header: Header<T, unknown>) => {
    if (setSortedColumnHeader) {
      const columnHeader = getSubtitleFromHeader(header, intl);
      setSortedColumnHeader(columnHeader);
    }

    header.column.getToggleSortingHandler()?.(event);
  };

  const handleRowClick = (row: Row<T>) => {
    if (row.getCanExpand()) {
      row.toggleExpanded();
    } else if (onRowClick) {
      onRowClick(row);
    }
  };

  const handleHover = (columnId: string) => {
    setMouseHoverState({
      [columnId]: true,
    });
  };

  const handleLeave = () => {
    setMouseHoverState({});
  };

  const isNonLabelColumn = (columnId: string): boolean => {
    const id = columnId.toLowerCase();
    return id.includes('image') || id.includes('number');
  };

  const renderGlobalExpandButton = () => (
    <button className="cursor-pointer">
      {table.getIsSomeRowsExpanded() ? (
        <TbIcon name="ChevronDown" size="md" onClick={() => table.toggleAllRowsExpanded(false)} />
      ) : (
        <TbIcon name="ChevronRight" size="md" onClick={() => table.toggleAllRowsExpanded(true)} />
      )}
    </button>
  );

  const reorderStackedColumnHeaders = <T,>(
    header: Header<T, unknown>,
    orgClones: (HeaderGroup<T> & { headers: Header<T, unknown>[] })[],
    targetGroups: HeaderGroup<T>[],
    level: number
  ) => {
    if (header.isPlaceholder) {
      let rowCounter = level + 1;
      while (rowCounter < orgClones.length) {
        const subRowHeader = orgClones[rowCounter].headers.shift();
        if (subRowHeader && !subRowHeader.isPlaceholder) {
          subRowHeader.rowSpan = rowCounter + 1;
          targetGroups[level].headers.push(subRowHeader);
          break;
        }
        rowCounter++;
      }
    } else {
      header.rowSpan = 1;
      targetGroups[level].headers.push(header);
      header.subHeaders.forEach(subHeader => {
        reorderStackedColumnHeaders(subHeader, orgClones, targetGroups, level + 1);
        orgClones[level + 1].headers.shift();
      });
    }
  };

  // Reorder multi header rows into the correct order to render using row spans
  const reorderColumnHeaderGroups = <T,>(originalGroups: HeaderGroup<T>[]) => {
    const orgClones = originalGroups.map(group => ({ ...group, headers: [...group.headers] }));
    const targetGroups: HeaderGroup<T>[] = originalGroups.map(group => ({ ...group, headers: [] }));

    orgClones[0].headers.forEach(header => {
      reorderStackedColumnHeaders(header, orgClones, targetGroups, 0);
    });

    return targetGroups;
  };

  const reorderedHeaderGroups = useMemo(() => reorderColumnHeaderGroups(table.getHeaderGroups()), [allHeaders]);

  return (
    <div>
      <div className="mt-4 rounded-lg overflow-hidden border">
        <table className="w-full table-fixed" data-testid={`${testIdPrefix ? testIdPrefix + '-' : ''}data-table`}>
          <thead>
            {reorderedHeaderGroups.map((headerGroup, idx) => (
              <tr key={headerGroup.id} className="border-b h-[60px]">
                {table.getCanSomeRowsExpand() && (
                  <th className="w-8">
                    <div className="flex items-center justify-center">{renderGlobalExpandButton()}</div>
                  </th>
                )}
                {headerGroup.headers.map((header, index) => (
                  <th
                    key={header.id}
                    rowSpan={header.rowSpan}
                    colSpan={header.colSpan}
                    className={cn(
                      'border-r max-w-[180px]',
                      header.column.id.toLowerCase().includes('image') && 'w-16 pl-1'
                    )}
                  >
                    <div
                      className={cn(
                        'flex justify-between items-center h-[48px] pl-2 pr-1 text-left text-sm font-bold',
                        header.column.getCanSort() && 'cursor-pointer',
                        index === 0 && table.getCanSomeRowsExpand() && 'pl-0'
                      )}
                      onMouseEnter={() => handleHover(header.column.id)}
                      onMouseLeave={handleLeave}
                    >
                      <div className="flex items-center w-full">
                        <div
                          className={`flex justify-between items-center w-full ${idx === 0 ? '' : 'font-normal'}`}
                          onClick={e => updateSortingState(e, header)}
                        >
                          {getHeaderText(header)}
                          {header.column.getCanSort() &&
                            (header.column.getIsSorted() || mouseHoverState[header.column.id]) && (
                              <button
                                type="button"
                                className="cursor-pointer select-none hover:bg-[#f7f7f7] p-1.5 rounded"
                              >
                                {{
                                  asc: <TbIcon name="SortingAscending" size="md" />,
                                  desc: <TbIcon name="SortingDescending" size="md" />,
                                }[header.column.getIsSorted() as string] ?? (
                                  <TbIcon name="SortingDescending" size="md" className="text-[#d1d1d1]" />
                                )}
                              </button>
                            )}
                        </div>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => {
              const firstLabelIndex = row.getVisibleCells().findIndex(c => !isNonLabelColumn(c.column.id));
              return (
                <tr
                  key={row.id}
                  className={cn(
                    'border-b h-[60px]',
                    row.getCanExpand() ? 'hover:bg-denim-50 hover:cursor-pointer' : '',
                    onRowClick ? 'group/row hover:bg-denim-50 hover:cursor-pointer' : ''
                  )}
                  onClick={() => handleRowClick(row)}
                >
                  {table.getCanSomeRowsExpand() && (
                    <td>
                      {row.getCanExpand() && (
                        <div className="flex justify-center items-center">
                          {row.getIsExpanded() ? (
                            <TbIcon name="ChevronDown" size="md" />
                          ) : (
                            <TbIcon name="ChevronRight" size="md" />
                          )}
                        </div>
                      )}
                    </td>
                  )}
                  {row.getVisibleCells().map((cell, index) => (
                    <td className="border-r text-sm break-words text-text-primary" key={cell.id}>
                      <div
                        className={cn(
                          'px-2',
                          index === 0 && row.getCanExpand() ? 'pl-0' : '',
                          index === firstLabelIndex && onRowClick
                            ? 'group-hover/row:text-denim-500 underline group-hover/row:font-semibold'
                            : ''
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!hidePagination && (
        <TablePagination
          page={pagination.pageIndex}
          rowsPerPage={pagination.pageSize}
          totalCount={table.getCoreRowModel().rows.length}
          onPageChange={(_event, newPage) => table.setPageIndex(newPage)}
          onRowsPerPageChange={(event: SelectChangeEvent<number>) => {
            table.setPageSize(Number(event.target.value));
            table.setPageIndex(0);
          }}
          rowsPerPageLabelId="table.pagination.rows_per_page"
        />
      )}
    </div>
  );
}
